import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { type LoginResponse, OidcSecurityService } from "angular-auth-oidc-client";
import * as _ from "lodash";
import { concat, ignoreElements, Observable, of, timer } from "rxjs";
import { catchError, exhaustMap, map, retry, switchMap, take, tap, timeout } from "rxjs/operators";

import { AlertType } from "../../components/alert-modal/alert";
import { MarketplaceUserStatus } from "../../definitions/marketplace-user-status";
import { AlertService } from "../../services/alert.service";
import { LegalRecordService } from "../../services/api/legal-record.service";
import { MpCommonService } from "../../services/api/mp-common.service";
import { LOGOUT_MARKER_KEY } from "../../services/auth/auth-storage";
import { ClockService } from "../../services/clock.service";
import { isTransientHttpError } from "../../shared/http-error.utils";
import {
	AcceptAgreements,
	AgreementsAcceptedFailed,
	AgreementsAcceptedSuccess,
	LoginFailed,
	LoginProcessed,
	LoginSucceeded,
	LogoutRequested,
	NavRequested,
	UserStatusLoaded,
	UserStatusRequested,
	UserStatusRequestFailed,
	UserStatusUpdated
} from "../actions/user.actions";
import { UserState } from "../models/user-state";
import { UserStatusState } from "../models/user-status-state";
import { AppState } from "../reducers";
import { SelectRoles } from "../selectors/user.selectors";

/**
 * Shown by the portal-home gate when /get-status can't be loaded. Deliberately not the raw
 * HTTP message, which is meaningless to a user.
 */
const USER_STATUS_LOAD_ERROR =
	"We're sorry, but we encountered a problem while loading the Global Data Marketplace. Please try refreshing your browser or returning in a few minutes. If this issue persists, please contact our support team, as this may indicate an account-specific issue.";

@Injectable()
export class UserEffects {
	constructor(
		private actions: Actions,
		private marketplaceService: MpCommonService,
		private clockService: ClockService,
		private router: Router,
		private store: Store<AppState>,
		private alert: AlertService,
		private legalRecordService: LegalRecordService,
		private oidcSecurityService: OidcSecurityService
	) {}

	loginSucceeded = createEffect(() =>
		this.actions.pipe(
			ofType(LoginSucceeded),
			switchMap(({ result }: { result: LoginResponse }) => {
				const { isAuthenticated, userData, accessToken, errorMessage } = result;

				if (!isAuthenticated || !accessToken) {
					return of(
						LoginFailed({
							error: errorMessage || "Authentication failed"
						})
					);
				}

				const accessPayload = this.decodeJWT(accessToken);
				const rawGroups: string[] = accessPayload?.["cognito:groups"] || [];
				// Cognito roles don't allow spaces, convert the _'s to spaces for legacy role support.
				const roles: string[] = rawGroups.map((role: string) => role.replace(/_/g, " "));

				const userState: UserState = {
					auth: true,
					roles: roles,
					userId: userData["cognito:username"] || userData.sub,
					loading: false
				};

				sessionStorage.setItem("currentUser", userState.userId);
				sessionStorage.setItem("roles", userState.roles.join(","));
				sessionStorage.setItem("needsRefresh", "true");

				this.clockService.start();

				const isSessionRestore = sessionStorage.getItem("sessionRestored") === "true";
				sessionStorage.removeItem("sessionRestored");

				if (!isSessionRestore) {
					void this.router.navigateByUrl("/app/dashboard");
				}

				return of(LoginProcessed({ userState }));
			}),
			catchError((error: Error) => {
				console.error("Error processing login:", error);
				return of(LoginFailed({ error: error.message }));
			})
		)
	);

	loginFailed = createEffect(
		() =>
			this.actions.pipe(
				ofType(LoginFailed),
				tap(({ error }) => {
					sessionStorage.clear();
					setTimeout(() => {
						void this.router.navigate(["/"]);
					}, 3000);
				})
			),
		{ dispatch: false }
	);

	logoutRequested = createEffect(
		() =>
			this.actions.pipe(
				ofType(LogoutRequested),
				// exhaustMap (not switchMap): LogoutRequested can fire from up to
				// five places (button, two dialogs, session-timeout service, and
				// SilentRenewFailed in AuthEventsService). switchMap would cancel
				// the first logout mid-redirect when a second action arrives,
				// leaving the user with cleared local storage but a still-valid
				// Cognito session cookie — they then get silently re-authenticated.
				exhaustMap(() =>
					// Resolve the end-session URL BEFORE tearing anything down.
					// It is built from the stored id token (id_token_hint), which
					// the teardown wipes; resolving it afterwards yields a weaker
					// logout URL.
					this.oidcSecurityService.getEndSessionUrl().pipe(
						// Cap a hung config/well-known lookup so a stuck request
						// can't leave the user sitting on an authenticated page.
						timeout(3_000),
						catchError((error) => {
							console.error("Could not resolve end-session URL:", error);
							return of(null);
						}),
						// Same reason: revocation reads the refresh token that the
						// teardown wipes, so it has to go out first — and it has to
						// complete before the navigation, or the browser cancels it.
						switchMap((endSessionUrl) =>
							this.revokeRefreshToken().pipe(map(() => endSessionUrl))
						),
						tap((endSessionUrl) => this.completeLogout(endSessionUrl))
					)
				),
				ignoreElements()
			),
		{ dispatch: false }
	);

	/**
	 * Tears the session down locally, then hard-navigates to Cognito's
	 * end-session endpoint so the Cognito session cookie is cleared too.
	 *
	 * Order matters. `logoffLocal()` resets the library's in-memory auth state
	 * and stops its check-session polling *without* redirecting — this effect
	 * owns the navigation so exactly one happens. (`logoff()` both redirects
	 * and resets, which meant the app's own follow-up navigation raced the
	 * library's and won with a URL that had already lost its id_token_hint.)
	 *
	 * Storage is wiped as the last thing before the assignment to
	 * `location.href`, leaving the smallest possible window for an in-flight
	 * silent renew to write fresh tokens back; `AuthBootstrapService` re-wipes
	 * on the way back in to cover the remainder.
	 *
	 * Falls back to "/" when the end-session URL cannot be resolved. That
	 * leaves the Cognito cookie intact, but local storage is gone either way,
	 * so the user is not left sitting on an authenticated page.
	 */
	private completeLogout(endSessionUrl: string | null): void {
		this.oidcSecurityService.logoffLocal();
		this.clockService.stop();
		this.clearAuthStorage();
		window.location.href = endSessionUrl ?? "/";
	}

	/**
	 * Revokes the refresh token at Cognito's revocation endpoint.
	 *
	 * The end-session endpoint only clears Cognito's session cookie; it leaves
	 * refresh tokens live. Without this call a logged-out session's refresh
	 * token keeps minting valid access tokens — no cookie required — until it
	 * expires on its own. Revoking also invalidates every access token issued
	 * from that refresh token, which is what makes a silent renew landing after
	 * the teardown harmless rather than a re-login.
	 *
	 * Only the refresh token is revoked: Cognito's `/oauth2/revoke` accepts
	 * refresh tokens only, and revoking one covers its access tokens anyway.
	 *
	 * Never fails the logout. A dead or disabled revocation endpoint is a
	 * reason to log the problem, not to strand the user on an authenticated
	 * page, so errors and hangs both fall through to the teardown.
	 */
	private revokeRefreshToken(): Observable<unknown> {
		return this.oidcSecurityService.revokeRefreshToken().pipe(
			timeout(3_000),
			catchError((error) => {
				console.error("Refresh token revocation failed:", error);
				return of(null);
			})
		);
	}

	navRequested = createEffect(
		() =>
			this.actions.pipe(
				ofType(NavRequested),
				switchMap((action) => {
					const navigationPath = this.buildNavigationPath(action);
					return of(
						this.router.navigate(navigationPath, action.navigationExtras || {})
					).pipe(
						tap(() => {
							_.forEach(action.alerts, (alert) => {
								switch (alert.type) {
									case AlertType.Info:
										this.alert.info(alert.message, null, alert.config);
										break;
									case AlertType.Success:
										this.alert.success(alert.message, null, alert.config);
										break;
									case AlertType.Warning:
										this.alert.warning(alert.message, null, alert.config);
										break;
									case AlertType.Error:
										this.alert.error(alert.message, null, alert.config);
										break;
								}
							});
						})
					);
				})
			),
		{ dispatch: false }
	);

	/**
	 * Originally, all of our routes were top-level.  Recently, routes have been moved  under "app", to prevent us from
	 * having to update all the links in the system, prepend "app" to the path.
	 */
	private buildNavigationPath(action: any): string[] {
		if (Array.isArray(action.path)) {
			if (action.doNotPrependApp === true) {
				return action.path;
			}
			if (action.path.length > 0 && action.path[0] !== "app") {
				return ["app", ...action.path];
			}
			return action.path;
		}

		const cleanPath = _.trimStart(action.path, "/");

		if (!cleanPath) {
			return action.doNotPrependApp === true ? [""] : ["app"];
		}

		if (cleanPath === "app" || cleanPath.startsWith("app/")) {
			return cleanPath.split("/");
		}

		return action.doNotPrependApp === true ? [cleanPath] : ["app", cleanPath];
	}

	userStatusRequested = createEffect(() =>
		this.actions.pipe(
			ofType(LoginProcessed, UserStatusUpdated),
			// take(1): `select` returns a stream that never completes, so without it this
			// switchMap stays subscribed for the life of the app and fires a fresh
			// /get-status every time the roles reference changes. `SessionRefreshed` is
			// dispatched on every navigation and its user reducer rebuilds state with
			// `_.merge(_.cloneDeep(state), …)`, producing a new roles array each time —
			// which made /get-status run on literally every page change.
			switchMap(() => this.store.pipe(select(SelectRoles), take(1))),
			switchMap((roles) =>
				concat(
					// Mark the request in flight so a navigation's SessionRefreshed can't
					// pass its cached snapshot off as the loaded answer while we wait.
					of(UserStatusRequested()),
					this.marketplaceService.userStatus().pipe(
						// /get-status occasionally fails right after login with a
						// transient gateway error (502/503/504) or network blip.
						// It clears immediately on a manual refresh, so it's not a
						// real per-user failure. Without a retry that single blip
						// dumps the user on the error screen. Retry a few times with
						// linear backoff so recovery happens transparently; only
						// retry transient errors so genuine failures (401/403/404/…)
						// still surface immediately.
						retry({
							count: 3,
							delay: (error: unknown, retryCount) => {
								if (isTransientHttpError(error)) {
									return timer(retryCount * 1_000);
								}
								throw error;
							}
						}),
						map((status) => {
							const userStatus = UserEffects.buildUserStatus(status, roles);
							sessionStorage.setItem("userStatus", JSON.stringify(userStatus));
							return UserStatusLoaded({ userStatus });
						}),
						catchError((error) => {
							// Drop the cached snapshot rather than persisting the failure:
							// sessionStorage is replayed by SessionRefreshed on every later
							// navigation, so writing the error here made one transient blip
							// flash the error panel for the rest of the tab's life.
							sessionStorage.removeItem("userStatus");
							console.error("Failed to load user status:", error);

							return of(UserStatusRequestFailed({ error: USER_STATUS_LOAD_ERROR }));
						})
					)
				)
			)
		)
	);

	acceptAgreements = createEffect(() =>
		this.actions.pipe(
			ofType(AcceptAgreements),
			switchMap((action) => {
				return this.legalRecordService.acceptLegalAgreements(action.agreementIds).pipe(
					map(() => {
						const currentUserStatus = JSON.parse(
							sessionStorage.getItem("userStatus") || "{}"
						);
						const updatedUserStatus = {
							...currentUserStatus,
							agreementsPendingAcceptance: []
						};
						sessionStorage.setItem("userStatus", JSON.stringify(updatedUserStatus));
						return AgreementsAcceptedSuccess();
					}),
					catchError((error) => of(AgreementsAcceptedFailed({ error })))
				);
			})
		)
	);

	private static buildUserStatus(
		status: MarketplaceUserStatus,
		roles: string[]
	): UserStatusState {
		const consumerUser = _.get(status, "consumerUser");
		const vendorUser = _.get(status, "vendorUser");
		const userStatus: UserStatusState = { loading: false };
		userStatus.type = "none";
		userStatus.onboardingStatus = status.onboardingStatus;
		userStatus.agreementsPendingAcceptance = status.agreementsPendingAcceptance;
		userStatus.allowAccessWithoutAgreementAcceptance =
			status.allowAccessWithoutAgreementAcceptance;
		userStatus.userSettings = status.userSettings;
		userStatus.consumerSettings = status.consumerSettings;
		userStatus.vendorSettings = status.vendorSettings;
		const isProposalApprover = roles.includes("Proposal Approver");
		if (consumerUser) {
			userStatus.id = consumerUser.consumerId;
			userStatus.type = "consumer";
			userStatus.status = consumerUser.accountStatus;
		} else if (vendorUser) {
			userStatus.id = vendorUser.vendorId;
			userStatus.type = "vendor";
			userStatus.status = vendorUser.accountStatus;
		} else if (isProposalApprover) {
			userStatus.status = "APPROVER";
			userStatus.type = "none";
		} else if (_.includes(roles, "Marketplace Admin")) {
			userStatus.status = "ADMIN";
		}
		return userStatus;
	}

	/**
	 * Wipes every trace of the session from browser storage.
	 *
	 * sessionStorage is cleared wholesale rather than by key pattern. The OIDC
	 * library keeps its entire token blob — authnResult, authzData, userData and
	 * the refresh token — under a single sessionStorage key equal to `configId`,
	 * which it defaults to `0-<clientId>`: a random Cognito client id such as
	 * "0-4c88ovhjeta51ci3s11clrd9of". That matches none of the substrings in
	 * {@link isAuthKey}, so the pattern sweep this replaces never deleted it.
	 * The blob then survived the round trip through Cognito (sessionStorage is
	 * per-tab, not per-navigation) and `checkAuth()` silently re-authenticated
	 * the user from it on the next page load — logging out appeared to do
	 * nothing until the user tried a second time. Nothing in sessionStorage is
	 * worth keeping past a logout, so there is no reason to be selective.
	 *
	 * localStorage keeps the pattern sweep: the library does not use it under
	 * the current configuration, but unrelated app state does, and clearing it
	 * wholesale would throw away preferences a logout has no business touching.
	 */
	private clearAuthStorage(): void {
		// These don't match isAuthKey's patterns, so without clearing them explicitly the
		// previous account's status and roles survive logout — and SessionRefreshed then
		// replays them over the next user's session until their /get-status lands.
		["userStatus", "currentUser", "roles", "needsRefresh"].forEach((key) => {
			sessionStorage.removeItem(key);
		});

		Object.keys(localStorage).forEach((key) => {
			if (this.isAuthKey(key)) {
				localStorage.removeItem(key);
			}
		});

		sessionStorage.clear();
		sessionStorage.setItem(LOGOUT_MARKER_KEY, "true");
	}

	private isAuthKey(key: string): boolean {
		const authPatterns = ["oidc", "auth", "token", "nonce", "state", "code_verifier", "pkce"];

		return authPatterns.some((pattern) => key.toLowerCase().includes(pattern));
	}

	private decodeJWT(token: string): any {
		try {
			const base64Url = token.split(".")[1];
			if (!base64Url) {
				return null;
			}

			const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
			const jsonPayload = decodeURIComponent(
				atob(base64)
					.split("")
					.map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
					.join("")
			);
			return JSON.parse(jsonPayload);
		} catch (e) {
			console.error("Error decoding JWT:", e);
			return null;
		}
	}
}
